import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { abrirCard, mensagemDaAbertura, type MotivoAbertura } from "../lib/abrir";
import { rotaDaFerramenta } from "../lib/dados";
import Carregando from "../componentes/Carregando";
import { Aviso } from "../componentes/ui";

// A ponte: pede o endereço à Edge Function e leva o aluno até lá.
//
// Por que uma PÁGINA e não um `onClick` no card. O endereço só existe depois de
// uma chamada de rede, e as três alternativas são piores:
//
// - `window.open` depois do `await`: o bloqueador de pop-up mata, porque a
//   abertura já não é mais gesto do usuário.
// - abrir a aba vazia no clique e escrever a url depois: passa pelo bloqueador,
//   mas a aba nasce com `window.opener` apontando para o hub, e a página de
//   destino é de terceiro — é o vetor de tabnabbing, com a nossa sessão do outro
//   lado.
// - navegar na mesma aba: perde o hub, e sem tela de espera o clique parece não
//   ter funcionado enquanto o Memberkit responde.
//
// Com uma rota, o card volta a ser um LINK de verdade: `target="_blank"` +
// `rel="noreferrer"` (aba nova sem `opener`), ctrl+clique e "abrir em nova aba"
// funcionam, o bloqueador não tem o que bloquear, e a espera tem cara de espera —
// o loader da marca, com o texto certo.
//
// A url de destino carrega credencial (token do Memberkit, sessão do CFC no
// fragmento). Por isso `location.replace` e não `assign`: a ponte sai do
// histórico, e o botão "voltar" não reabre um link que já foi usado.
export default function Abrir() {
  // `aula` só existe na rota dos cursos bônus (`/abrir/:curso/:card/:aula`,
  // 000177). Quando ela vem, o destino é a AULA dentro da escola em vez da raiz —
  // e a autorização é a mesma, resolvida pelo banco.
  const { curso, card, aula } = useParams<{ curso: string; card: string; aula?: string }>();
  const navegar = useNavigate();
  const [motivo, setMotivo] = useState<MotivoAbertura | null>(null);
  // StrictMode roda o efeito duas vezes em desenvolvimento. Sem esta trava, cada
  // clique geraria dois links mágicos e duas linhas em `aberturas_de_card` — e a
  // contagem "do mais usado ao menos usado" nasceria dobrada.
  const jaPediu = useRef(false);

  useEffect(() => {
    if (jaPediu.current) return;
    jaPediu.current = true;

    if (!card) {
      setMotivo("sem-acesso");
      return;
    }
    // Aula tem de ser um id, e o id vem do catálogo. Recusar aqui evita uma ida
    // de rede que já se sabe perdida — e mais: a Edge Function responderia 400
    // "aula inválida", que é uma mensagem de API, não de aluno.
    if (aula !== undefined && !/^[0-9]{1,18}$/.test(aula)) {
      setMotivo("aula-desconhecida");
      return;
    }

    (async () => {
      // 'conta' é o sentinela dos cards que não pertencem a curso (escopo
      // 'conta'); a function recebe `null` e o banco ignora o campo.
      const alvo = !curso || curso === "conta" ? null : curso;
      const r = await abrirCard(card, alvo, aula ?? null);
      if (r.ok) {
        if (r.interna) {
          // A ferramenta mora aqui dentro: navegação da SPA, sem recarregar o
          // bundle — link antigo para /abrir cai em pé em /ferramenta.
          navegar(rotaDaFerramenta(card), { replace: true });
          return;
        }
        window.location.replace(r.url);
        return;
      }
      setMotivo(r.motivo);
    })();
  }, [card, curso, aula]);

  if (motivo) {
    const { titulo, texto } = mensagemDaAbertura(motivo);
    // De onde o aluno veio decide para onde o botão volta. Vindo de uma AULA de
    // curso bônus, ele veio da lista de aulas — mandá-lo à página do curso o
    // faria procurar o card de novo. "Atualize a página" (a mensagem de
    // `aula-desconhecida`) só faz sentido com a lista à mão.
    const [para, rotulo] =
      aula && curso && curso !== "conta"
        ? [`/ferramenta/${card}?curso=${encodeURIComponent(curso)}`, "Voltar às aulas"]
        : curso && curso !== "conta"
          ? [`/curso/${curso}`, "Voltar ao curso"]
          : ["/", "Voltar ao início"];
    return (
      <Aviso
        titulo={titulo}
        acao={
          <Link to={para} className="pill-cta">
            {rotulo}
          </Link>
        }
      >
        {texto}
      </Aviso>
    );
  }

  return (
    <Carregando
      texto={aula ? "Abrindo a aula…" : card === "aulas" ? "Abrindo suas aulas…" : "Abrindo…"}
    />
  );
}
